import React, { useState, useEffect, useCallback } from 'react';
import { languages, getTranslation } from './languages';
import { Settings, User, Monitor, Smartphone, LogOut, Shield, Cpu, CheckCircle, Activity, Heart } from 'lucide-react';

export default function SettingsModal({ isOpen, onClose, userProfile, onSaveProfile, currentLanguage = 'en', authenticatedFetch }) {
    const [name, setName] = useState('');
    const [ageRange, setAgeRange] = useState('');
    const [educationLevel, setEducationLevel] = useState('');
    const [uiLanguage, setUILanguage] = useState(currentLanguage);
    const [preferredModel, setPreferredModel] = useState('gemini-3.8-flash');
    const [isSaving, setIsSaving] = useState(false);
    const [activeSessions, setActiveSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'aiModel', 'usage', or 'sessions'

    const t = useCallback((key, params = {}) => getTranslation(key, uiLanguage, params), [uiLanguage]);

    // Initialize form data from user profile
    useEffect(() => {
        if (userProfile) {
            setName(userProfile.name || '');
            // Use ageRange consistently
            const ageRangeValue = userProfile.ageRange || '';
            setAgeRange(ageRangeValue);
            setEducationLevel(userProfile.educationLevel || '');
            setUILanguage(userProfile.uiLanguage || 'en');
            setPreferredModel('gemini-3.8-flash');
        }
    }, [userProfile]);

    const fetchActiveSessions = useCallback(async () => {
        setIsLoadingSessions(true);
        try {
            const response = await authenticatedFetch('/api/active-sessions');
            if (response.ok) {
                const data = await response.json();
                if (data.error) {
                    console.error('[Settings] Session management error:', data.error);
                    // Show error message to user
                    setActiveSessions([]);
                } else {
                    setActiveSessions(data.sessions || []);
                }
            }
        } catch (error) {
            console.error('Error fetching active sessions:', error);
        } finally {
            setIsLoadingSessions(false);
        }
    }, [authenticatedFetch]);

    // Fetch active sessions when modal opens
    useEffect(() => {
        if (isOpen && activeTab === 'sessions' && authenticatedFetch) {
            fetchActiveSessions();
        }
    }, [isOpen, activeTab, authenticatedFetch, fetchActiveSessions]);
    
    // Listen for event to open AI Model tab
    useEffect(() => {
        const handleOpenAIModelTab = () => {
            if (isOpen) {
                setActiveTab('aiModel');
            }
        };
        
        window.addEventListener('openAIModelTab', handleOpenAIModelTab);
        return () => {
            window.removeEventListener('openAIModelTab', handleOpenAIModelTab);
        };
    }, [isOpen]);

    const handleLogoutSession = async (sessionId) => {
        if (!window.confirm(t('confirmLogoutSession'))) return;
        
        try {
            const response = await authenticatedFetch(`/api/session/${sessionId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                fetchActiveSessions();
            }
        } catch (error) {
            console.error('Error logging out session:', error);
        }
    };

    const handleLogoutAllSessions = async () => {
        if (!window.confirm(t('confirmLogoutAllSessions'))) return;
        
        try {
            const response = await authenticatedFetch('/api/logout-all-devices', {
                method: 'POST'
            });
            if (response.ok) {
                // This will log out the current session too
                window.location.reload();
            }
        } catch (error) {
            console.error('Error logging out all sessions:', error);
        }
    };

    const formatSessionDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(uiLanguage, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Calculate birth year range from age range
        const calculateAgeRange = (ageRange) => {
            if (!ageRange || ageRange === 'prefer_not_to_say') return null;
            
            const currentYear = new Date().getFullYear();
            const ageRanges = {
                'under20': { min: 0, max: 19 },
                '20-30': { min: 20, max: 30 },
                '31-40': { min: 31, max: 40 },
                '41-50': { min: 41, max: 50 },
                '51-60': { min: 51, max: 60 },
                'over60': { min: 61, max: 120 }
            };
            
            const range = ageRanges[ageRange];
            if (!range) return null;
            
            const minBirthYear = currentYear - range.max;
            const maxBirthYear = currentYear - range.min;
            return `${minBirthYear}-${maxBirthYear}`;
        };
        
        const ageRangeValue = ageRange ? calculateAgeRange(ageRange) : null;
        
        const profileData = {
            name: name || null,
            ageRange: ageRange || null,
            birthYear: ageRangeValue,
            educationLevel: educationLevel || null,
            uiLanguage: uiLanguage || 'en',
            preferredModel: preferredModel || 'gemini-3.8-flash',
            // Force Gemini provider in production
            llmProvider: 0
        };

        setIsSaving(true);
        try {
            await onSaveProfile(profileData);
            onClose();
        } catch (error) {
            console.error('Failed to save profile:', error);
        } finally {
            setIsSaving(false);
        }
    };


    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        // Prevent closing when clicking on the backdrop
        e.stopPropagation();
    };

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
            onMouseDown={handleBackdropClick}
        >
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in-up">
                <div className="flex items-center gap-3 mb-6">
                    <Settings size={24} className="text-sky-600" />
                    <h2 className="text-xl font-semibold text-gray-800">{t('settings')}</h2>
                </div>

                {/* Tabs */}
                <div className="flex gap-4 mb-6 border-b">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`pb-2 px-1 font-medium transition-colors ${
                            activeTab === 'profile'
                                ? 'text-sky-600 border-b-2 border-sky-600'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <User size={16} />
                            {t('profile')}
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('aiModel')}
                        className={`pb-2 px-1 font-medium transition-colors ${
                            activeTab === 'aiModel'
                                ? 'text-sky-600 border-b-2 border-sky-600'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Cpu size={16} />
                            {t('aiModel')}
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('usage')}
                        className={`pb-2 px-1 font-medium transition-colors ${
                            activeTab === 'usage'
                                ? 'text-sky-600 border-b-2 border-sky-600'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Activity size={16} />
                            {t('usage')}
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('sessions')}
                        className={`pb-2 px-1 font-medium transition-colors ${
                            activeTab === 'sessions'
                                ? 'text-sky-600 border-b-2 border-sky-600'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Shield size={16} />
                            {t('activeSessions')}
                        </div>
                    </button>
                </div>

                {/* Profile Tab Content */}
                {activeTab === 'profile' && (
                    <div className="space-y-6">
                    {/* Name */}
                    <div>
                        <label className="text-sm font-bold text-gray-600 block mb-2">
                            {t('name')} <span className="text-gray-400 font-normal">({t('optional')})</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('enterYourName')}
                            className="w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                            autoComplete="off"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('nameHelp')}</p>
                    </div>

                    {/* Age Range */}
                    <div>
                        <label className="text-sm font-bold text-gray-600 block mb-2">
                            {t('ageRange')} <span className="text-gray-400 font-normal">({t('optional')})</span>
                        </label>
                        <select
                            value={ageRange}
                            onChange={(e) => setAgeRange(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                            <option value="">{t('selectAgeRange')}</option>
                            {Object.entries(t('ageRanges')).map(([key, value]) => (
                                <option key={key} value={key}>{value}</option>
                            ))}
                            <option value="" disabled>──────────</option>
                            <option value="prefer_not_to_say">{t('preferNotToSay')}</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">{t('ageRangeHelp')}</p>
                    </div>

                    {/* Education Level */}
                    <div>
                        <label className="text-sm font-bold text-gray-600 block mb-2">
                            {t('educationLevel')} <span className="text-gray-400 font-normal">({t('optional')})</span>
                        </label>
                        <select
                            value={educationLevel}
                            onChange={(e) => setEducationLevel(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                            <option value="">{t('selectEducation')}</option>
                            {Object.entries(t('educationLevels')).map(([key, value]) => (
                                <option key={key} value={key}>{value}</option>
                            ))}
                            <option value="" disabled>──────────</option>
                            <option value="prefer_not_to_say">{t('preferNotToSay')}</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">{t('educationLevelHelp')}</p>
                    </div>

                    {/* UI Language */}
                    <div>
                        <label className="text-sm font-bold text-gray-600 block mb-2">
                            {t('uiLanguage')} <span className="text-red-500 font-normal">*</span>
                        </label>
                        <select
                            value={uiLanguage}
                            onChange={(e) => setUILanguage(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                            {Object.entries(languages).map(([key, lang]) => (
                                <option key={key} value={key}>{lang.nativeName}</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">{t('youCanChangeLater')}</p>
                    </div>
                </div>
                )}

                {/* AI Model Tab Content */}
                {activeTab === 'aiModel' && (
                    <div className="space-y-6">
                        <div className="bg-sky-50 rounded-lg p-5 text-center">
                            <div className="font-semibold text-gray-800">Google Gemini 3.8 Flash</div>
                        </div>
                    </div>
                )}

                {/* Usage Tab Content */}
                {activeTab === 'usage' && (
                    <div className="space-y-6">
                        {/* Thank You Card */}
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center p-3 bg-sky-50 rounded-full mb-4">
                                <Heart className="text-sky-600" size={28} fill="currentColor" />
                            </div>
                            
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                {t('thankYouTestUser')}
                            </h3>
                            
                            <p className="text-gray-600 max-w-md mx-auto">
                                {t('thankYouMessage', { appTitle: t('appTitle') })}
                            </p>
                        </div>
                                
                        {/* Usage Status Card */}
                        <div className="border border-gray-200 rounded-lg p-5">
                            <h4 className="font-medium text-gray-700 mb-4 text-center">{t('testUserBenefits')}</h4>
                            <div className="space-y-1 max-w-sm mx-auto">
                                <div className="flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 rounded-md transition-colors">
                                    <span className="text-gray-700 flex items-center gap-2">
                                        <CheckCircle size={16} className="text-green-500" />
                                        {t('conversations')}
                                    </span>
                                    <span className="font-medium text-gray-900">{t('unlimited')}</span>
                                </div>
                                <div className="flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 rounded-md transition-colors">
                                    <span className="text-gray-700 flex items-center gap-2">
                                        <CheckCircle size={16} className="text-green-500" />
                                        {t('conceptGeneration')}
                                    </span>
                                    <span className="font-medium text-gray-900">{t('unlimited')}</span>
                                </div>
                                <div className="flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 rounded-md transition-colors">
                                    <span className="text-gray-700 flex items-center gap-2">
                                        <CheckCircle size={16} className="text-green-500" />
                                        {t('aiLearningSummary')}
                                    </span>
                                    <span className="font-medium text-gray-900">{t('unlimited')}</span>
                                </div>
                                <div className="flex items-center justify-between py-2.5 px-3 hover:bg-gray-50 rounded-md transition-colors">
                                    <span className="text-gray-700 flex items-center gap-2">
                                        <CheckCircle size={16} className="text-green-500" />
                                        {t('reviewScheduling')}
                                    </span>
                                    <span className="font-medium text-gray-900">{t('unlimited')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sessions Tab Content */}
                {activeTab === 'sessions' && (
                    <div className="space-y-6">
                        {isLoadingSessions ? (
                            <div className="text-center py-8">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
                                <p className="mt-2 text-gray-600">{t('loadingSessions')}</p>
                            </div>
                        ) : (
                            <>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <h3 className="font-semibold text-amber-800 mb-2">{t('activeSessionsInfo')}</h3>
                                    <p className="text-sm text-amber-700">
                                        {t('activeSessionsDescription')}
                                    </p>
                                </div>

                                {activeSessions.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        {t('noActiveSessions')}
                                    </div>
                                ) : (
                                    <>
                                        <div className="space-y-3">
                                            {activeSessions.map((session) => (
                                                <div
                                                    key={session.session_id}
                                                    className={`border rounded-lg p-4 ${
                                                        session.isCurrent
                                                            ? 'border-sky-300 bg-sky-50'
                                                            : 'border-gray-200 bg-white'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-1">
                                                                {session.device_info?.includes('Mobile') ? (
                                                                    <Smartphone size={20} className="text-gray-600" />
                                                                ) : (
                                                                    <Monitor size={20} className="text-gray-600" />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <div className="font-medium text-gray-800">
                                                                    {session.device_info || t('unknownDevice')}
                                                                    {session.isCurrent && (
                                                                        <span className="ml-2 text-xs text-sky-600 font-normal">
                                                                            ({t('currentSession')})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-sm text-gray-600 mt-1">
                                                                    {t('ipAddress')}: {session.ip_address || t('unknown')}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {t('lastActive')}: {formatSessionDate(session.last_activity)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {!session.isCurrent && (
                                                            <button
                                                                onClick={() => handleLogoutSession(session.session_id)}
                                                                className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-md transition-colors"
                                                                title={t('logoutThisDevice')}
                                                            >
                                                                <LogOut size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="pt-4 border-t">
                                            <button
                                                onClick={handleLogoutAllSessions}
                                                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                            >
                                                <LogOut size={16} />
                                                {t('logoutAllDevices')}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-4 mt-8">
                    {(activeTab === 'profile' || activeTab === 'aiModel') ? (
                        <>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSaving}
                                className="px-4 py-2 rounded-md text-white bg-sky-500 hover:bg-sky-600 disabled:opacity-50"
                            >
                                {isSaving ? t('saving') : t('saveChanges')}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200"
                        >
                            {t('close')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
} 